import Controller, { inject as controller } from '@ember/controller';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import clsx from 'clsx';
import { restartableTask, task } from 'ember-concurrency';
import { v1 } from 'ember-uuid';
import { ASSIGNMENT } from 'sh-www/models/assignment';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';
import { POSTING_CREATE__QUERY_PARAMS } from 'sh-www/constants/routes/posting-create';
import { STATIC_STATISTICS_CREATOR } from 'sh-www/constants/static-statistics';
import PRICING_PLANS from 'sh-www/constants/pricing-plans';
import ICON_PATH from 'sh-www/constants/assets/icons';
import { formatNumber } from 'sh-www/helpers/format-number';
import { SEARCH_REQUEST_PAGE_ERROR } from "sh-www/constants/routes/people-center";

const SEARCH_REQUEST_PAGES_TO_PRELOAD = 2;
const SEARCH_REQUEST_PAGES_TO_PRELOAD_ON_PAGE_ADVANCE = 2;
const PAGE_SIZE = 10;
const SEARCH_RADIUS = 250;
const SEARCH_RESULTS_CONSIDERED_SHORT_MAX = 5

const INLCUDES = [
  'location',
  'last-location',
  'projectdigests:sort(embed-preferred):limit(1)',
  'projectdigests.company',
  'projectdigests.image',
  'projectdigests.services',
  'projectdigests.oembed',
  'projectdigests.review.reviewer',
  'projectdigests.classifications',
  'experiences',
  'services:sort(most-work)',
  'worked-with-brands.image',
  'user-statistics',
];

const SEARCH_UPGRADE_PROMPT = {
  title: 'Find creative freelancers',
  image: ICON_PATH.FIND_CREATORS,
  type: PRICING_PLANS.UPGRADE_REQUEST_SEARCH,
};

class SearchController extends Controller {
  /**
   * Register services
   */
  @service('state-managers/application') applicationStateManager;
  @service('commands.user-commands') userCommandService;
  @service constants;
  @service errorTracker;
  @service router;
  @service session;
  @service shData;
  @service store;

  /** @type {Ember.Controller} */
  @controller application;

  creatorEstimate = formatNumber([STATIC_STATISTICS_CREATOR.COUNT]);

  hasPerformedASearch = false;
  allServices = [];
  selectedServices = [];
  searchHeaderInputName = '';
  searchFilterByPastHired = false;
  searchFilterBySaved = false;
  searchRequestMaxRate = '';
  searchRequestLocationName = '';
  searchRequestLocation = null;

  searchRequestParams = null;
  searchRequestTimestamp = null;
  pageCurrentlyBrowsing = null;
  searchRequestResults = {};
  searchRequestPagesRequested = [];

  pagination = null;

  @computed(
    'isPeopleListEmpty',
    'isPeopleListError',
  )
  get searchClassNames() {
    return clsx(
      'SH-r-people-center--dark-mode',
      this.isPeopleListEmpty && 'is-people-list-empty-active',
      this.isPeopleListError && 'is-people-list-error-active'
    );
  }

  get upgradePrompt() {
    return {
      text: `Search our creative marketplace of over ${this.creatorEstimate} Creators when you upgrade to a subscription plan`,
      ...SEARCH_UPGRADE_PROMPT,
    };
  }

  /** @type {User|null} */
  profileOverlayCurrentUser = null;

  @computed(
    'profileOverlayCurrentUser',
    'searchRequestResults',
    'pageCurrentlyBrowsing',
  )
  get hasPrevProfileOverlayUser() {
    const {
      searchRequestResults,
      profileOverlayCurrentUser,
      pageCurrentlyBrowsing
    } = this;

    // There is no previous page to navigate to.
    if (!searchRequestResults || !profileOverlayCurrentUser) {
      return false;
    }

    // Past the first page
    if (pageCurrentlyBrowsing > 1) {
      return true;
    }

    const currentPage = searchRequestResults[pageCurrentlyBrowsing];
    const currentIndex = currentPage.indexOf(profileOverlayCurrentUser);

    // Past the first index on the first page
    return currentIndex > 0;
  }

  @computed(
    'profileOverlayCurrentUser',
    'searchRequestResults',
    'pageCurrentlyBrowsing',
  )
  get hasNextProfileOverlayUser() {
    const {
      searchRequestResults,
      profileOverlayCurrentUser,
      pageCurrentlyBrowsing
    } = this;

    // There is no next page to navigate to.
    if (!searchRequestResults || !profileOverlayCurrentUser) {
      return false;
    }

    // Not on the last page
    if (pageCurrentlyBrowsing < Object.keys(searchRequestResults).length) {
      return true;
    }

    const currentPage = searchRequestResults[pageCurrentlyBrowsing];
    const currentIndex = currentPage.indexOf(profileOverlayCurrentUser);

    // Is on last index of last page
    return currentIndex < currentPage.length - 1;
  }

  /**
   * @type {Boolean} Whether to show the search upgrade prompt.
   */
  @computed(
    'session.currentUser.company.features.can_search',
  )
  get showSearchUpgradePrompt() {
    return !this.session.currentUser.company?.features?.can_search;
  }

  @computed('pageCurrentlyBrowsing', 'searchRequestResults')
  get currentPeopleList() {
    const { pageCurrentlyBrowsing, searchRequestResults } = this;

    if (pageCurrentlyBrowsing === null || !searchRequestResults) {
      return [];
    }

    return searchRequestResults[pageCurrentlyBrowsing];
  }

  @computed('currentPeopleList.[]')
  get isPeopleListEmpty() {
    const { currentPeopleList } = this;

    // If we have an error state, return false
    if (currentPeopleList === SEARCH_REQUEST_PAGE_ERROR) {
      return false;
    }

    // We use .get('length') here because we're working with an ArrayProxy
    return currentPeopleList === undefined ? true : !currentPeopleList.get('length');
  }

  @computed('currentPeopleList.[]')
  get isPeopleListShort() {
    const { currentPeopleList } = this;

    // If we have an error state, return false
    if (currentPeopleList === SEARCH_REQUEST_PAGE_ERROR) {
      return false;
    }

    return currentPeopleList === undefined
      ? false
      : currentPeopleList.length > 0 && currentPeopleList.length <= SEARCH_RESULTS_CONSIDERED_SHORT_MAX;
  }

  @computed('currentPeopleList.[]')
  get isPeopleListError() {
    const { currentPeopleList } = this;
    return (currentPeopleList === SEARCH_REQUEST_PAGE_ERROR);
  }

  @computed('beginFreelancerSearch.last.args.@each.{text,followed_by,past_hire}')
  get shouldSuggestPostingCreation() {
    const lastRunArgs = this.beginFreelancerSearch.last?.args[0];

    // only suggest if they're not looking for a specific person
    return !lastRunArgs.text
      && !lastRunArgs.followed_by
      && !lastRunArgs.past_hire;
  }

  @restartableTask
  *beginFreelancerSearch(params) {
    // Generate timestamp for comparison checks in additional pages.  If the user
    // performs multiple searches in quick succession, we don't want any
    // pre-fetched pages from the first search propagating to later searches
    const timestamp = Date.now();

    // Begin loading the first page
    this.setProperties({
      searchRequestParams: params,
      searchRequestTimestamp: timestamp,
      searchRequestPagesRequested: [ params.page ],
      searchRequestResults: {},
    });

    const request = this.generateFreelancerSearchRequest(params);
    const pageNum = params.page;

    yield this.processRequest(request, timestamp, pageNum);
    const currentSearchTimestamp = this.searchRequestTimestamp;

    // Make sure the timestamps are consistent before loading up additional pages
    if (currentSearchTimestamp === timestamp) {
      this.fetchPreloadedPages(params, timestamp);
    }

    // First page has safely finished loading
    this.set('hasPerformedASearch', true);
  }

  @task
  *loadServicesTask() {
    const services = yield this.store.findAll('service');
    this.set('allServices', services);
  }

  @action
  beginNewFreelancerSearch(userName, services, location, maxRate, isSaved, pastHire) {
    const fullParams = {
      count: PAGE_SIZE,
      radius: SEARCH_RADIUS,
      include: [
        ...INLCUDES,
        `company-seller-connections:company_id(${this.session.currentUser.company.id})`
      ].join(','),
      cache_key: v1(),
      text: userName || undefined,
      services: services.length ? services : undefined,
      locations: location ? [`${location.lat},${location.lng}`] : undefined,
      location_id: location?.id ?? undefined,
      meets_budget: maxRate || undefined,
      followed_by: isSaved,
      past_hire: pastHire,
      page: 1,
    };

    return this.beginFreelancerSearch.perform(fullParams);
  }

  @action
  processPageChange(newPage) {
    this.changePage(newPage);
  }

  @action
  handleSearchHeaderInputNameChanged(name) {
    this.set('searchHeaderInputName', name);
  }

  @action
  handleServiceAdded(addedService) {
    this.set('selectedServices', [ ...this.selectedServices, addedService ]);
  }

  @action
  handleServiceRemoved(removedService) {
    this.set('selectedServices', this.selectedServices.filter(service => service !== removedService));
  }

  @action
  handleFilterByPastHireClicked(isPastHire) {
    this.set('searchFilterByPastHired', isPastHire);
  }

  @action
  handleFilterBySavedClicked(isSaved) {
    this.set('searchFilterBySaved', isSaved);
  }

  @action
  handleMaxRateChanged(rate) {
    this.set('searchRequestMaxRate', rate);
  }

  @action
  handleLocationChange(location) {
    this.setProperties({
      searchRequestLocationName: location?.formattedAddress ?? '',
      searchRequestLocation: location ?? null,
    });
  }

  @action
  handleLocationInput(value) {
    this.set('searchRequestLocationName', value);
  }

  @action
  createPostingFromSearch() {
    const {
      searchRequestLocation,
      searchRequestMaxRate,
      selectedServices,
    } = this;

    const fillRate = searchRequestMaxRate || null;

    this.router.transitionTo(EMBER_ROUTE_NAMES.POSTING__CREATE, {
      queryParams: {
        [POSTING_CREATE__QUERY_PARAMS.SERVICE_IDS]: selectedServices?.map(({ id }) => id).join(',') ?? null,
        [POSTING_CREATE__QUERY_PARAMS.LOCATION_ID]: searchRequestLocation?.id ?? null,
        [POSTING_CREATE__QUERY_PARAMS.MAX_RATE]: fillRate,
        [POSTING_CREATE__QUERY_PARAMS.RATE_TYPE]: fillRate ? ASSIGNMENT.RATE_TYPE.DAY : null,
      },
    });
  }

  @action
  handleSetProfileOverlay(profileOverlay) {
    this.set('application.profileOverlay', profileOverlay);
  }

  @action
  handleProfileOverlayClose() {
    this.set('application.profileOverlay', null);
  }

  /**
   * @param {User|Number} value
   */
  @action
  handleSetProfileOverlayUser(value) {
    if (!Number.isInteger(value)) {
      // The provided value is the user model itself.
      const newUser = value;
      this.set('profileOverlayCurrentUser', newUser);
      return;
    }

    const {
      searchRequestResults,
      pageCurrentlyBrowsing,
      profileOverlayCurrentUser
    } = this;

    const currentPage = searchRequestResults[pageCurrentlyBrowsing];
    const currentIndex = currentPage.indexOf(profileOverlayCurrentUser);

    // The provided value is the offset from the current user (1 = next, -1 = previous).
    const newIndex = currentIndex + value;

    // If navigating backwards from the first item on a page...
    if (newIndex < 0) {
      // There is no previous page to navigate to.
      if (pageCurrentlyBrowsing === 0) return;

      // Use the last item of the previous page.
      const newPageIndex = pageCurrentlyBrowsing - 1;
      const previousPage = searchRequestResults[newPageIndex];
      const newUser = previousPage.objectAt(previousPage.length - 1);
      this.set('profileOverlayCurrentUser', newUser);
      this.changePage(newPageIndex);
      return;
    }

    // If navigating forwards from the last item on a page...
    if (newIndex > currentPage.length - 1) {
      // There is a next page to navigate to.
      if (pageCurrentlyBrowsing < Object.keys(searchRequestResults).length) {
        // Use the first item of the next page.
        const newPageIndex = pageCurrentlyBrowsing + 1;
        const nextPage = searchRequestResults[newPageIndex];
        const newUser = nextPage.objectAt(0);
        this.set('profileOverlayCurrentUser', newUser);
        this.changePage(newPageIndex);
      }

      return;
    }

    // If navigating within the bounds of the current page, simply use the item at the new index.
    const newUser = currentPage.objectAt(newIndex);
    this.set('profileOverlayCurrentUser', newUser);
  }

  buildAndProcessAdditionalPageRequest(searchParams, page, timestamp) {
    // Page was already requested
    if (this.searchRequestPagesRequested.includes(page)) {
      return;
    }

    // Add the new page to PagesRequested array
    this.set('searchRequestPagesRequested', [ ...this.searchRequestPagesRequested, page ]);

    const request = this.generateFreelancerSearchRequest({ ...searchParams, page });
    return this.processRequest(request, timestamp, page);
  }

  loadAdditionalPages(firstPage, lastPage, searchParamsRaw = null, timestampRaw = null) {
    const timestamp = timestampRaw === null
      ? this.searchRequestTimestamp
      : timestampRaw;

    const searchParams = searchParamsRaw === null
      ? this.searchRequestParams
      : searchParamsRaw;

    // Forward direction
    if (firstPage < lastPage) {
      for (let i = firstPage; i <= lastPage; i++) {
        this.buildAndProcessAdditionalPageRequest(searchParams, i, timestamp);
      }
      return;
    }

    // Backward direction
    if (firstPage > lastPage) {
      for (let i = firstPage; i >= lastPage; i-- ) {
        this.buildAndProcessAdditionalPageRequest(searchParams, i, timestamp);
      }
      return;
    }

    // Only one page is being pulled
    this.buildAndProcessAdditionalPageRequest(searchParams, firstPage, timestamp);
  }

  generateFreelancerSearchRequest(params) {
    const fullParams = { ...params };

    if (fullParams['filter'] === undefined) {
      fullParams['filter'] = {};
    }

    if (fullParams['searchNetwork'] !== undefined) {
      fullParams.filter['extendedNetwork'] = this.session.currentUser.company.id;
      delete fullParams['searchNetwork'];
    }

    if (fullParams['locations'] !== undefined) {
      fullParams.filter['location'] = [
        fullParams['locations'][0],
      ];
      delete fullParams['locations'];

      if (fullParams['radius'] !== undefined) {
        fullParams.filter['location'].push(fullParams['radius']);
        delete fullParams['radius'];
      }
    }

    if (fullParams['services']) {
      fullParams.filter['has_one_of_services'] = fullParams['services'].map(({ id }) => id);
      delete fullParams['services'];
    }

    if (fullParams['text']) {
      fullParams.filter['name'] = fullParams['text'];
      delete fullParams['text'];
    }

    if (fullParams['meets_budget']) {
      fullParams.filter['meets_budget'] = fullParams['meets_budget'];
      delete fullParams['meets_budget'];
    }

    if (fullParams['past_hire']) {
      fullParams.filter['past_hire'] = this.session.currentUser.company.id;
      delete fullParams['past_hire'];
    }

    if (fullParams['followed_by']) {
      fullParams.filter['followed_by'] = this.session.currentUser.company.id;
      delete fullParams['followed_by'];
    }

    fullParams.filter['is_active_in_last_year'] = true;
    fullParams.filter['verified'] = true;
    fullParams.filter['rating_greater_than'] = 0;
    fullParams.filter['marketplace'] = true;
    fullParams.sort = 'marketplace';
    fullParams.track = true;

    return this.store.query('user', fullParams);
  }

  fetchPreloadedPages(searchParams, searchTimestamp) {
    const pageNum = searchParams.page;
    const { pagination } = this;

    // If there aren't any results or only 1 page of results, no need to load any more
    if (!pagination?.totalPages || pagination.totalPages === 1) {
      return;
    }

    // Pick a useful range of pages to preload.
    if (pageNum > 1) {
      // We've encountered a search that begins on a page other than zero (a back-button search)
      const pagesInEitherDirection = Math.floor(SEARCH_REQUEST_PAGES_TO_PRELOAD / 2);

      const lowerHalfMinPage = Math.max(1, pageNum - pagesInEitherDirection);
      const lowerHalfMaxPage = pageNum - 1;

      const upperHalfMinPage = pageNum + 1;
      const upperHalfMaxPage = pageNum + pagesInEitherDirection;

      this.loadAdditionalPages(lowerHalfMaxPage, lowerHalfMinPage, searchParams, searchTimestamp); // pages will be retrieved counting down
      this.loadAdditionalPages(upperHalfMinPage, upperHalfMaxPage, searchParams, searchTimestamp); // pages will be retrieved counting up
    } else {
      const minPage = pageNum + 1;
      const maxPage = Math.min(pageNum + SEARCH_REQUEST_PAGES_TO_PRELOAD, pagination.totalPages);

      this.loadAdditionalPages(minPage, maxPage, searchParams, searchTimestamp);
    }
  }

  processRequest(request, searchTimestamp, pageNum) {
    return request.then(
      (pageOfResults) => {
        const currentSearchTimestamp = this.searchRequestTimestamp;

        // Ensure timestamps are consistent before processing results
        if (currentSearchTimestamp === searchTimestamp) {
          const pagination = this.shData.getPaginationModelFromResponse(pageOfResults)

          if (!pagination) {
            // Throw some kind of error or whatever
            console.error('processPageOfSearchResults: Bad pagination data!');
          }

          // Check if we're loading the first page of results
          if (this.beginFreelancerSearch.isRunning) {
            // Set the page currently being browsed
            this.setProperties({
              pagination,
              pageCurrentlyBrowsing: pagination.currentPage
            });
          }

          // Store the current page of results in a local array
          const searchRequestResults = { ...this.searchRequestResults };
          searchRequestResults[pagination.currentPage] = pageOfResults;
          this.set('searchRequestResults', { ...searchRequestResults });
        }

      },
      (error) => {
        const { pageCurrentlyBrowsing, searchRequestResults } = this;

        // Set the requested page to an error state
        searchRequestResults[pageNum] = SEARCH_REQUEST_PAGE_ERROR;

        // error may have occurred loading first page
        if (!pageCurrentlyBrowsing) {
          this.set('pageCurrentlyBrowsing', pageNum);
        }

        this.set('searchRequestResults', { ...searchRequestResults });

        this.errorTracker.error('Failed to perform user search', this, error);
        return Promise.reject(false);
      },
    );
  }

  changePage(newPage) {
    const currentPage = this.pageCurrentlyBrowsing;

    // You cannot change the page when pageCurrentlyBrowsing is non numeric.
    if (typeof currentPage !== 'number') {
      return;
    }

    if (newPage > currentPage) {
      this.inspectAndUpdatePreloadedPagesForward(newPage);
    } else {
      this.inspectAndUpdatePreloadedPagesBackward(newPage);
    }

    this.set('pageCurrentlyBrowsing', newPage);

    const appHeader = document.querySelector('.js-app-header');
    const peopleListFilterContainer = document.querySelector('.SH-c-people-center-search-header');
    const appHeaderHeight = appHeader.offsetHeight;

    const peopleListFiltersOffset = peopleListFilterContainer.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({
      top: peopleListFiltersOffset - appHeaderHeight,
    });
  }

  inspectAndUpdatePreloadedPagesBackward(newPage) {
    const { pagination, searchRequestResults } = this;

    if (!pagination?.totalPages || !searchRequestResults) {
      return;
    }

    const minLoadedPage = Number.parseInt(
      Object.keys(searchRequestResults)
        .sort((a, b) => Number.parseInt(a) - Number.parseInt(b))
        .shift()
    );

    // Attempt to fetch new pages when we're within two pages of the minIndex (note: if a page had already
    // been requested, it won't be loaded twice -- check happens in _buildAndProcessRequest())
    const shouldLoadMorePages = (newPage - minLoadedPage <= 2 && minLoadedPage > 1);

    if (shouldLoadMorePages) {
      const firstPage = Math.max(minLoadedPage - 1, 1);
      const lastPage = Math.max(minLoadedPage - SEARCH_REQUEST_PAGES_TO_PRELOAD_ON_PAGE_ADVANCE, 1);

      this.loadAdditionalPages(firstPage, lastPage);
    }
  }

  inspectAndUpdatePreloadedPagesForward(newPage) {
    const { pagination, searchRequestResults } = this;

    if (!pagination?.totalPages || !searchRequestResults) {
      return;
    }

    const maxLoadedPage = Number.parseInt(
      Object.keys(searchRequestResults)
      .sort((a, b) => Number.parseInt(b) - Number.parseInt(a))
      .shift()
    );

    // Attempt to fetch new pages when we're within two pages of the maxIndex (note: if a page had already
    // been requested, it won't be loaded twice -- check happens in _buildAndProcessRequest())
    const shouldLoadMorePages = (maxLoadedPage - newPage <= 2 && maxLoadedPage < pagination.totalPages);

    if (shouldLoadMorePages) {
      const firstPage = Math.min(maxLoadedPage + 1, pagination.totalPages);
      const lastPage = Math.min(maxLoadedPage + SEARCH_REQUEST_PAGES_TO_PRELOAD_ON_PAGE_ADVANCE, pagination.totalPages);

      this.loadAdditionalPages(firstPage, lastPage);
    }
  }

  resetSearchParams() {
    this.setProperties({
      hasPerformedASearch: false,
      allServices: [],
      selectedServices: [],
      searchHeaderInputName: '',
      searchFilterByPastHired: false,
      searchFilterBySaved: false,
      searchRequestMaxRate: '',
      searchRequestLocationName: '',
      searchRequestLocation: null,
      searchRequestParams: null,
      searchRequestTimestamp: null,
      pageCurrentlyBrowsing: null,
      searchRequestResults: {},
      searchRequestPagesRequested: [],
      pagination: null,
    });
  }
}

export default SearchController;
